import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography, Row, Col, Spin, Empty, Alert, Input, Select, Button } from 'antd';
import { ShoppingOutlined, SearchOutlined, EnvironmentOutlined, ClearOutlined, StarOutlined } from '@ant-design/icons';
import { useStores } from '../../stores';
import ProductCard from '../../components/products/ProductCard';
import BackButton from '../../components/BackButton';
import { getAnimalNameWithEmoji } from '../../utils/animalEmojis';
import { getCityByLanguage } from '../../utils/locationFormat';
import './ProductsPage.scss';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const ProductsPage = observer(() => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { productStore, petStore } = useStores();

  useEffect(() => {
    // Fetch categories, cities, and animal categories
    productStore.fetchCategories();
    productStore.fetchCities();
    petStore.fetchAnimalCategories();
  }, [productStore, petStore]);

  useEffect(() => {
    // Check for partner filter in URL
    const partnerParam = searchParams.get('partner');

    // Reset partner filter if not in URL
    if (!partnerParam && productStore.filters.partner) {
      productStore.updateFilter('partner', null);
    } else if (partnerParam) {
      productStore.updateFilter('partner', partnerParam);
    }

    // Fetch products when component mounts or when URL/language changes
    const params = {};
    if (partnerParam) {
      params.partner = partnerParam;
    }
    productStore.fetchProducts(params);
  }, [productStore, searchParams, i18n.language]);

  const handleSearch = (value) => {
    productStore.updateFilter('search', value);
  };

  const handleCategoryChange = (value) => {
    productStore.updateFilter('category', value || null);
  };

  const handleAnimalCategoryChange = (value) => {
    productStore.updateFilter('animalCategory', value || null);
  };

  const handleCityChange = (value) => {
    productStore.updateFilter('city', value || null);
  };

  const handleSortChange = (value) => {
    productStore.updateFilter('sortBy', value);
  };

  const handleRatingChange = (value) => {
    productStore.updateFilter('minRating', value || null);
  };

  const handleResetFilters = () => {
    productStore.resetFilters();
    // Clear URL params and refetch
    if (searchParams.get('partner')) {
      navigate('/products', { replace: true });
    }
    productStore.fetchProducts();
  };

  // Get localized category name
  const getCategoryName = (category) => {
    return i18n.language === 'el' ? (category.name_el || category.name_en) : category.name_en;
  };

  const products = productStore.filteredProducts;

  return (
    <div className="products-page">
      {/* Show back button when coming from partner profile */}
      {searchParams.get('partner') && (
        <div style={{ paddingTop: 16 }}>
          <BackButton label={t('common.back')} />
        </div>
      )}

      <div className="products-header">
        <h1><ShoppingOutlined /> {t('products.title')}</h1>
        <p>{t('products.description')}</p>
      </div>

        {/* Filters Row */}
        <div className="products-filters">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={24} md={5}>
              <Search
                placeholder={t('products.filters.searchPlaceholder')}
                allowClear
                enterButton={<SearchOutlined />}
                size="large"
                value={productStore.filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                onSearch={handleSearch}
              />
            </Col>

            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder={t('products.filters.category')}
                size="large"
                style={{ width: '100%' }}
                allowClear
                onChange={handleCategoryChange}
                value={productStore.filters.category || undefined}
              >
                {productStore.categories.map((category) => (
                  <Option key={category.id} value={category.id}>
                    {getCategoryName(category)}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder={t('products.filters.location')}
                size="large"
                style={{ width: '100%' }}
                allowClear
                showSearch
                optionFilterProp="children"
                onChange={handleCityChange}
                value={productStore.filters.city || undefined}
              >
                {productStore.cities.map((city) => (
                  <Option key={city} value={city}>
                    <EnvironmentOutlined style={{ marginRight: 4 }} />
                    {getCityByLanguage(city, i18n.language)}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder={t('products.filters.animalType')}
                size="large"
                style={{ width: '100%' }}
                allowClear
                showSearch
                optionFilterProp="children"
                onChange={handleAnimalCategoryChange}
                value={productStore.filters.animalCategory || undefined}
              >
                {petStore.categories.map((category) => (
                  <Option key={category.id} value={category.id}>
                    {getAnimalNameWithEmoji(i18n.language === 'el' ? (category.name_el || category.name_en) : category.name_en)}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={3}>
              <Select
                placeholder={t('products.filters.minRating')}
                size="large"
                style={{ width: '100%' }}
                allowClear
                onChange={handleRatingChange}
                value={productStore.filters.minRating || undefined}
              >
                <Option value={4}><StarOutlined /> 4+</Option>
                <Option value={3}><StarOutlined /> 3+</Option>
                <Option value={2}><StarOutlined /> 2+</Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} md={3}>
              <Select
                placeholder={t('products.filters.sortBy')}
                size="large"
                style={{ width: '100%' }}
                onChange={handleSortChange}
                value={productStore.filters.sortBy || 'name'}
              >
                <Option value="name">{t('products.filters.sortNameAZ')}</Option>
                <Option value="price_asc">{t('products.filters.sortPriceLowHigh')}</Option>
                <Option value="price_desc">{t('products.filters.sortPriceHighLow')}</Option>
                <Option value="rating_desc">{t('products.filters.sortRatingHighest')}</Option>
              </Select>
            </Col>

            <Col xs={24} sm={24} md={1}>
              <Button
                icon={<ClearOutlined />}
                size="large"
                onClick={handleResetFilters}
                title={t('common.clearAll')}
              />
            </Col>
          </Row>
        </div>

        {/* Error Alert */}
        {productStore.error && (
          <Alert
            message={t('common.error')}
            description={productStore.error}
            type="error"
            closable
            style={{ marginBottom: 24 }}
          />
        )}

        {/* Products Grid */}
        {productStore.loading ? (
          <div className="loading-container">
            <Spin size="large" tip={t('products.loading')} />
          </div>
        ) : products.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                {t('products.noProducts')}
              </span>
            }
          />
        ) : (
          <Row gutter={[24, 24]} className="products-grid">
            {products.map(product => (
              <Col
                key={product.id}
                xs={24}
                sm={12}
                md={8}
              >
                <ProductCard product={product} />
              </Col>
            ))}
          </Row>
        )}
    </div>
  );
});

export default ProductsPage;
